import { AccountInfo } from "@azure/msal-browser";
import { Photo, SyncStatus } from "../types/Photo";
import { productIdFor } from "../utils/product";
import { isQueued } from "../utils/queue";
import { oneDriveService } from "./oneDriveService";
import { photoStorageService } from "./photoStorageService";

export interface SyncFailure {
  filename: string;
  reason: string;
}

/**
 * Ce qui s'est réellement passé pendant un envoi. `sent` ne compte que les
 * photos effectivement parties ; tout le reste est dans `failed`, avec sa cause.
 */
export interface SyncReport {
  sent: number;
  failed: SyncFailure[];
}

/** Le plafond de la cadence de repli : cinq minutes entre deux tentatives. */
const RETRY_CEILING_MS = 5 * 60 * 1000;

/**
 * La cadence de repli : rang de tentative vers délai avant la suivante.
 *
 * 30 s, puis 1 min, 2 min, et le doublement bute sur cinq minutes — une panne
 * durable ne doit pas vider la batterie du téléphone. Fonction pure, séparée de
 * la boucle qui l'applique : c'est ce qui permet de l'éprouver seule, sans
 * horloge factice ni minuteur dans les tests.
 */
export const retryDelay = (attempt: number): number =>
  Math.min(30_000 * 2 ** Math.max(0, attempt - 1), RETRY_CEILING_MS);

/**
 * Le tag des réveils demandés au navigateur pour le service worker.
 */
const WAKE_TAG = "drain-queue";

/**
 * Le plancher demandé à `periodicsync` : au plus une notification par
 * demi-journée. Un plancher, pas un horaire — Chrome ne déclenche
 * `periodicsync` que sur une PWA installée et lancée comme application
 * distincte, avec un score d'engagement non nul, et sur un réseau connu de
 * l'appareil ; il choisit seul s'il déclenche et quand.
 */
const PERIODIC_WAKE_MS = 12 * 60 * 60 * 1000;

/** Les deux réveils d'arrière-plan, absents des types du DOM. */
interface BackgroundSyncRegistration extends ServiceWorkerRegistration {
  sync?: { register(tag: string): Promise<void> };
  periodicSync?: {
    register(tag: string, options?: { minInterval: number }): Promise<void>;
  };
}

/**
 * La file ne s'est pas vidée : on arme le service worker pour qu'il prévienne,
 * et c'est ici — et nulle part ailleurs — qu'on demande la permission de
 * notifier. Au premier lancement, la question aurait porté sur une chose que
 * l'opérateur n'a pas encore rencontrée ; ici, elle porte sur des photos qui
 * sont réellement restées.
 *
 * Tout y est facultatif. Permission refusée, réveils indisponibles : rien ne
 * casse, et les cinq réveils de la page restent le mécanisme principal. Aucun
 * jeton n'est transmis au service worker : il lira la file, il n'enverra pas.
 */
async function armBackgroundWake(): Promise<void> {
  const registration = (await navigator.serviceWorker?.ready) as
    | BackgroundSyncRegistration
    | undefined;
  if (!registration) return;

  try {
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
    await registration.sync?.register(WAKE_TAG);
  } catch (error) {
    console.info("Réveil du service worker indisponible :", error);
  }

  // Demandé à part : un refus du filet ne doit pas emporter le réveil au retour
  // du réseau, qui, lui, marche partout.
  try {
    await registration.periodicSync?.register(WAKE_TAG, {
      minInterval: PERIODIC_WAKE_MS,
    });
  } catch (error) {
    console.info("Réveil périodique indisponible :", error);
  }
}

/** Message court et lisible pour un échec d'envoi. */
export function describeSyncError(error: unknown): string {
  const err = error as {
    statusCode?: number;
    code?: string;
    message?: string;
  } | null;
  const parts = [
    err?.statusCode ? `HTTP ${err.statusCode}` : "",
    err?.code ?? "",
    err?.message ?? String(error),
  ].filter(Boolean);
  return parts.join(" — ").slice(0, 600);
}

class SyncService {
  private isSyncing = false;
  /**
   * La file : le dernier tour promis. Un nouvel envoi s'y enchaîne au lieu de
   * s'exclure, si bien que deux clôtures rapprochées ne se marchent pas dessus
   * et que les lots partent un à la fois, dans l'ordre d'arrivée.
   */
  private tail: Promise<unknown> = Promise.resolve();
  private listeners = new Set<() => void>();
  /**
   * Le compte dont la file se draine. C'est lui que les réveils envoient : sans
   * compte connu, un retour de réseau n'a rien à reprendre.
   */
  private account: AccountInfo | null = null;
  /** Le minuteur de la prochaine tentative. Il meurt avec la file. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Le rang de la prochaine tentative, remis à zéro quand la file se vide. */
  private attempt = 0;

  constructor() {
    // Les réveils appartiennent au service d'envoi, pas aux composants : ceux-ci
    // ne connaissent que deux gestes, enfiler et s'abonner. Posés dans des
    // effets React, ces écouteurs exigeraient une seconde couture de test, en
    // React, sans prior art dans ce dépôt.
    if (typeof window === "undefined") return;
    window.addEventListener("online", () => this.wake());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.wake();
    });
  }

  /**
   * Le compte actif change : c'est le réveil du démarrage.
   *
   * Un compte neuf remet toute la file en marche, ce qui reprend les photos
   * laissées en cours par une session fermée en plein envoi. MSAL rend un objet
   * neuf à chaque rafraîchissement de jeton : on le garde, mais on ne redraine
   * pas pour autant.
   */
  setAccount(account: AccountInfo | null): void {
    const previous = this.account?.homeAccountId;
    this.account = account;
    if (!account) {
      this.stopRetrying();
      return;
    }
    if (account.homeAccountId !== previous) void this.startSync(account);
  }

  /** Un réveil : toute la file repart, sans qu'aucun geste ne l'ait cherchée. */
  private wake(): void {
    if (this.account) void this.startSync(this.account);
  }

  /**
   * S'abonner aux changements d'état des photos, pour relire la file sans la
   * sonder. Rend de quoi se désabonner.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    // ponytail : chaque changement d'état déclenche une relecture complète chez
    // les abonnés. Regrouper les notifications d'un même drainage si la fenêtre
    // de sept jours devient assez lourde pour que la relecture se sente.
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (error) {
        console.error("Abonné à la file d'envoi en erreur :", error);
      }
    }
  }

  /**
   * Met un lot dans la file d'envoi et rend son rapport quand son tour est
   * passé : ce qui est parti, ce qui a échoué et pourquoi.
   *
   * Reste attendable, mais plus personne n'a à l'attendre — un appelant qui
   * l'abandonne laisse simplement l'envoi se poursuivre derrière lui. Deux
   * appels qui se chevauchent ne s'excluent plus : le second s'enfile.
   *
   * @param account - Le compte dont on envoie les photos
   * @param photoIds - Ids des photos à envoyer. Absent : toute la file du compte.
   */
  startSync(account: AccountInfo, photoIds?: string[]): Promise<SyncReport> {
    this.account = account;
    const turn = this.tail.then(() => this.drain(account, photoIds));
    // Le maillon retenu pour la suite est neutralisé : un tour qui lève ne doit
    // pas empêcher le suivant de venir, ni laisser un rejet sans preneur.
    this.tail = turn.catch(() => undefined);
    return turn;
  }

  /**
   * Un tour de file : le lot demandé part, une photo à la fois.
   */
  private async drain(
    account: AccountInfo,
    photoIds?: string[]
  ): Promise<SyncReport> {
    this.isSyncing = true;
    try {
      const all = await photoStorageService.getPhotosByAccount(
        account.homeAccountId
      );
      const ids = photoIds ?? [];
      const named = ids.length > 0;
      const requested = named
        ? all.filter((photo) => ids.includes(photo.id))
        : all;
      const photosToSync = requested.filter(isQueued);

      const report: SyncReport = { sent: 0, failed: [] };

      // Une photo demandée **nommément** mais non reprenable est un échec, pas
      // un silence. Un drainage de toute la file, lui, n'a rien demandé : les
      // photos déjà arrivées y sont la normalité, pas une anomalie à rapporter.
      if (named) {
        for (const photo of requested) {
          if (!photosToSync.includes(photo)) {
            report.failed.push({
              filename: photo.filename,
              reason: `Non reprenable : état « ${photo.syncStatus} ».`,
            });
          }
        }
        for (const id of ids) {
          if (!all.some((photo) => photo.id === id)) {
            report.failed.push({
              filename: id,
              reason: "Photo absente de la file.",
            });
          }
        }
      }

      // Les dossiers déjà résolus pendant ce tour. Trois photos d'un même
      // produit coûtaient trois résolutions du même dossier ; le cache meurt
      // avec le tour, donc rien n'a jamais à être invalidé.
      const folders = new Map<string, string>();

      for (const photo of photosToSync) {
        try {
          await this.syncPhoto(account, photo, folders);
          report.sent++;
        } catch (error) {
          const reason = describeSyncError(error);
          console.error(`Échec de l'envoi de ${photo.filename} :`, error);
          await photoStorageService.updateSyncStatus(
            photo.id,
            SyncStatus.FAILED,
            reason
          );
          this.notify();
          report.failed.push({ filename: photo.filename, reason });
        }
      }

      return report;
    } finally {
      this.isSyncing = false;
      await this.scheduleRetry(account);
      // Le tour est fini : l'indicateur cesse de tourner même si rien n'a bougé.
      this.notify();
    }
  }

  /**
   * Après un tour, la file retente d'elle-même tant qu'elle ne s'est pas vidée.
   *
   * Le rang de tentative se remet à zéro et le minuteur meurt dès qu'il ne
   * reste plus rien à faire partir : l'application ne travaille pas dans le
   * vide. Rien ici n'écrit « envoyée » sur quoi que ce soit — ce qui était
   * interdit, ce n'est pas de retenter, c'est de rassurer : entre deux
   * tentatives l'état affiché reste celui de l'échec.
   */
  private async scheduleRetry(account: AccountInfo): Promise<void> {
    this.stopRetrying();

    let queued: boolean;
    try {
      queued = (
        await photoStorageService.getPhotosByAccount(account.homeAccountId)
      ).some(isQueued);
    } catch (error) {
      // Relire la file a échoué : le tour, lui, a bien eu lieu et son rapport
      // ne doit pas s'en trouver changé. Le prochain réveil relira.
      console.error("Lecture de la file pour la reprise :", error);
      return;
    }
    if (!queued) {
      this.attempt = 0;
      return;
    }

    // La file ne s'est pas vidée : le service worker prendra le relais si le
    // téléphone est rangé avant qu'elle ne le soit.
    void armBackgroundWake();

    this.attempt++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.startSync(account);
    }, retryDelay(this.attempt));
  }

  /** Le minuteur meurt : plus rien ne repartira de soi-même. */
  private stopRetrying(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  /**
   * Envoie une photo. Toute erreur remonte à l'appelant.
   */
  private async syncPhoto(
    account: AccountInfo,
    photo: Photo,
    folders: Map<string, string>
  ): Promise<void> {
    await photoStorageService.updateSyncStatus(photo.id, SyncStatus.SYNCING);
    this.notify();

    // Le produit est le chemin distant : ses photos arrivent groupées sous
    // `AAAA-MM-JJ/HHMMSS/`, pas en vrac dans le dossier du jour. Sans produit
    // — rattachement manqué — la date de création fait foi.
    const productFolder = photo.productId || productIdFor(photo.createdAt);
    let folderId = folders.get(productFolder);
    if (folderId === undefined) {
      folderId = await oneDriveService.getOrCreateFolder(
        account,
        productFolder
      );
      folders.set(productFolder, folderId);
    }
    await oneDriveService.uploadPhoto(
      account,
      folderId,
      photo.filename,
      photo.fullBlob
    );

    // `SYNCED` n'est écrit que sur la preuve rendue par `uploadPhoto`. La photo
    // reste sur l'appareil : c'est la rétention de sept jours qui l'en retire,
    // et son état est désormais le seul endroit où l'envoi se lit.
    await photoStorageService.updateSyncStatus(photo.id, SyncStatus.SYNCED);
    this.notify();
  }

  /**
   * Returns whether a sync is currently in progress
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }
}

export const syncService = new SyncService();
